import hyperid from 'hyperid';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getCookieConsentValue } from 'react-cookie-consent';
import { useSelector } from 'react-redux';
import AccordionList from '../AccordionList';
import { NewsDetailHeader } from '../NewsDetail';
import { getCookieConsent } from '../../redux/global/global.selector';
import style from './Article.module.scss';
import Player from '../Player';
import QuoteCarousel from '../QuoteCarousel';
import ReactModal from 'react-modal';
import { DownloadCard, DownloadCardContent } from '../DownloadComponents';
import AcceptTermsPageContent from '../AcceptTermsPageContent';

const StatsComponent = ({ title, stats }) => {
    return (
        <div className={style.fourColsInfo}>
            {title && <h2 className={style.titleStats}>{title}</h2>}
            <div className={style.stats}>
                {stats.map((stats, id) => (
                    <div className={style.statsInfo} key={id}>
                        <div className={['h1', style.statsTitle].join(' ')}>{stats.title}</div>
                        <div
                            className={style.statsDesc}
                            dangerouslySetInnerHTML={{
                                __html: stats.description,
                            }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const Quotes = ({ quotes }) => {
    return (
        <div>
            <QuoteCarousel
                quotes={quotes}
            />
        </div>
    )
};

const QuoteItem = ({ authorName, authorRole, quote }) => {
    return (
        <div className={style.quoteWrapper}>
            <div className={style.quote}>
                <div className={style.quoteTitle}>{quote}</div>
                <div className={style.quoteRow}>
                    <div className={style.quoteAuthor}>
                        <div className={style.quoteAuthorName}>{authorName}</div>
                        <div className={style.quoteAuthorRole}>{authorRole}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const VideoItem = ({ coverImage, video }) => {
    return (
        <div className={style.video}>
            <Player key={video} poster={coverImage?.url} link={video} />
        </div>
    );
};

const Article = data => {
    const { title, header, leftContents, rightContents, acceptedTermsPageContent } = data;
    const downloadContents = rightContents?.filter(item => item.type === 'newsDetailDownloadContent');
    const download = rightContents?.filter(item => item.type === 'newsDetailDownload');
    const containerRef = useRef(null);
    const idGenerator = hyperid();
    header.share = {
        title: 'SHARE',
        items: [
            {
                title: 'twitter',
                icon: 'twitter2',
            },
            {
                title: 'linkedin',
                icon: 'linkedin2',
            },
        ],
    };
    header.date = header.publishingDate;
    const requireDownloadAcceptTerms = header.requireDownloadAcceptTerms;
    const [downloadUserAccepted, setDownloadUserAccepted] = useState(false);
    const downloadAcceptButtonText = header.downloadAcceptButtonText;
    const downloadRejectTermsRedirectionUrl = header.downloadRejectTermsRedirectionUrl;
    const toggleUserAcceptDownload = useCallback(() => {
        setDownloadUserAccepted(!downloadUserAccepted);
    }, [downloadUserAccepted]);

    const BiographyList = ({ title, biographies: items }) => {
        const accordions = items.map(item => {
            const title = BiographyItem(item);
            return {
                title,
                content: item.bodyCopy,
                readLabelOpen: 'BIO',
                readLabelClosed: 'BIO',
            };
        });
        return (
            <div className={style.biographyList}>
                <h4 className={style.title}>{title}</h4>
                <div className={style.biographyItems}>
                    <AccordionList className={style.accordionList} accordions={accordions} noPadding={true} />
                </div>
            </div>
        );
    };

    const BiographyItem = item => {
        const bioItemRef = useRef(null);
        if (!item.id) {
            item.id = idGenerator();
        }
        return (
            <>
                <div className={[style.biographyItem].join(' ')} key={item.id}>
                    <div className={style.biographyImageWrapper} ref={bioItemRef}>
                        <img className={style.biographyImage} src={item?.image?.url || '/images/bioImageDefault.jpg'} />
                    </div>
                    <div className={style.biographyAuthor}>
                        <div className={style.biographyName}>{item?.name}</div>
                        <div className={style.biographyRole}>{item?.role}</div>
                    </div>
                </div>
            </>
        );
    };

    const TextFormat = content => {
        return (
            <div className={style.articleText}>
                <h4>{content?.title}</h4>
                <div
                    className={style.articleDiv}
                    dangerouslySetInnerHTML={{
                        __html: content?.body,
                    }}
                ></div>
            </div>
        );
    };

    const ImageFormat = content => {
        return (
            <div className={style['article-image']}>
                <img src={content.image.url} alt={content.image.alt} />
            </div>
        );
    };

    const ContactCard = ({ title, name, role, phone, email }) => {
        return (
            <div className={style.contact}>
                <div className={style['contact-title']}>{title}</div>
                {name.value && (
                    <div className={style['contact-item']}>
                        <div className={style['contact-item-title']}>{name.title}</div>
                        <div className={style['contact-item-value']}>{name.value}</div>
                    </div>
                )}
                {role.value && (
                    <div className={style['contact-item']}>
                        <div className={style['contact-item-title']}>{role.title}</div>
                        <div className={style['contact-item-value']}>{role.value}</div>
                    </div>
                )}
                {phone.value && (
                    <div className={style['contact-item']}>
                        <div className={style['contact-item-title']}>{phone.title}</div>
                        <div className={style['contact-item-value']}>
                            <a href={`tel:${phone.value}`} rel='noopener noreferrer'>
                                {phone.value}
                            </a>
                        </div>
                    </div>
                )}
                {email.value && (
                    <div className={style['contact-item']}>
                        <div className={style['contact-item-title']}>{email.title}</div>
                        <div className={style['contact-item-value']}>
                            {email.value.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i) ? (
                                <a href={`mailto:${email.value}`} rel='noopener noreferrer'>
                                    {email.value}
                                </a>
                            ) : (
                                <>{email.value}</>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    ContactCard.propTypes = {
        title: PropTypes.string,
        name: PropTypes.object,
        role: PropTypes.object,
        phone: PropTypes.object,
        email: PropTypes.object,
    };

    const renderChildComponent = component => {
        const { type } = component;
        switch (type) {
            case 'insightsBiographyList':
                return BiographyList(component);
            case 'insightsPodcastWidgetItem':
                return <div className={style.podcast} dangerouslySetInnerHTML={{ __html: component.codeSnippet }} />;
            case 'newsDetailText':
                return TextFormat(component);
            case 'insightsQuoteItem':
                return QuoteItem(component);
            case 'statementsAndStats':
                return StatsComponent({
                    ...component,
                });
            case 'newsDetailQuote':
                return Quotes(component)
            case 'newsDetailVideo':
                return VideoItem(component);
            case 'newsDetailImage':
                return <div className={style.articleImage}>
                    <ImageFormat {...component} />
                </div>
            case 'newsDetailContact':
                return <ContactCard {...component} />
            default:
                return '';
        }
    };

    const [currentUrl, setCurrentUrl] = useState('');
    const [sharethis, setSharethis] = useState('none');
    const cookieAccept = useSelector(getCookieConsent);

    useEffect(() => {
        setCurrentUrl(window.location.href);
        if (window.__sharethis__ && getCookieConsentValue() === 'true') {
            window.__sharethis__.initialize();
        }
    }, []);

    useEffect(() => {
        setSharethis(getCookieConsentValue() === 'true' ? 'block' : 'none');
    }, [cookieAccept]);

    const shareContent = {};

    return (
        <>
            <article className={style.el} ref={containerRef}>
                <div className={[style.container, 'container'].join(' ')}>
                    <NewsDetailHeader
                        header={header}
                        sharethis={sharethis}
                        content={shareContent}
                        currentUrl={currentUrl}
                    />
                    <div className={style.twoColumnsWrapper}>
                        <div className={style.leftColumn}>
                            <h1>{title}</h1>
                            {leftContents.map(content => renderChildComponent(content))}
                        </div>
                        <div className={style.rightColumn}>
                            {rightContents.map(content => renderChildComponent(content))}
                            {requireDownloadAcceptTerms && !downloadUserAccepted && (
                                <div className={style.button}>
                                    <button
                                        type='button'
                                        className={style.acceptButton}
                                        onClick={toggleUserAcceptDownload}
                                    >
                                        <span className={style.title}>{downloadAcceptButtonText}</span>
                                    </button>
                                </div>
                            )}
                            {requireDownloadAcceptTerms && !downloadUserAccepted && downloadRejectTermsRedirectionUrl && downloadRejectTermsRedirectionUrl.link?.url && (
                                <div className={style.button}>
                                    <button
                                        type='button'
                                        className={style.rejectButton}
                                        onClick={() => { window.location.href = downloadRejectTermsRedirectionUrl.link.url; }}
                                    >
                                        <span className={style.title}>{downloadRejectTermsRedirectionUrl.text}</span>
                                    </button>
                                </div>
                            )}
                            {!requireDownloadAcceptTerms &&
                                downloadContents?.map((item, i) => <DownloadCardContent key={i} {...item} />)}
                            {!requireDownloadAcceptTerms &&
                                download?.map((item, i) => <DownloadCard {...item} key={`download-${i}`} />)}
                        </div>
                    </div>
                </div>
            </article>
            <ReactModal
                isOpen={!!downloadUserAccepted}
                onRequestClose={toggleUserAcceptDownload}
                className='page-modal'
                overlayClassName='page-modal-overlay'
                contentLabel='Modal'
                closeTimeoutMS={800}
            >
                <button type='button' className='close-btn' onClick={toggleUserAcceptDownload}>
                    <span />
                    <span />
                </button>
                <div className={[style.el, style.modalEl].join(' ')}>
                    <div className={[style.modalContent, style.content].join(' ')}>
                        <div className={[style.modalContentWrapper, style.contentWrapper].join(' ')}>
                            {acceptedTermsPageContent ? (
                                <AcceptTermsPageContent
                                    acceptedTermsPageContent={acceptedTermsPageContent}
                                    currentUrl={currentUrl}
                                />
                            ) : (
                                <>
                                    {((requireDownloadAcceptTerms && downloadUserAccepted) ||
                                        !requireDownloadAcceptTerms) &&
                                        downloadContents?.map((item, i) => <DownloadCardContent key={i} {...item} />)}
                                    {((requireDownloadAcceptTerms && downloadUserAccepted) ||
                                        !requireDownloadAcceptTerms) &&
                                        download?.map((item, i) => <DownloadCard {...item} key={`download-${i}`} />)}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </ReactModal>
        </>
    );
};

Article.propTypes = {
    type: PropTypes.string,
    title: PropTypes.string,
    header: PropTypes.string,
    contentsInner: PropTypes.array,
    contents: PropTypes.array,
    portfolio: PropTypes.object,
    biographies: PropTypes.array,
    acceptedTermsPageContent: PropTypes.object,
};

StatsComponent.propTypes = {
    stats: PropTypes.array,
    title: PropTypes.string,
};

QuoteItem.propTypes = {
    authorName: PropTypes.string,
    authorRole: PropTypes.string,
    quote: PropTypes.string,
};
Quotes.propTypes = {
    quotes: PropTypes.array
};
VideoItem.propTypes = {
    coverImage: PropTypes.object,
    video: PropTypes.string
}

export default Article;
